import { Timestamp, collection, doc, getDoc, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore'
import { uniformIndex, wordToken } from '@racedash/shared'
import { db } from '../lib/firebase'

/**
 * Three words, like donkey-butter-kite. The token is the document id of both
 * the share link and its public projection, so it is the entire capability;
 * see shareToken.ts for why three words is enough, and why words at all.
 *
 * The list is loaded here and only here, so a dashboard never downloads it.
 */
async function newToken(): Promise<string> {
  const { words } = await import('./words')
  return wordToken(words, (n) => uniformIndex(n))
}

/**
 * A token nobody has, by asking first. Three random words collide too rarely
 * to plan around and too often to ignore across years of links, and a set on
 * an existing id would quietly repoint somebody's link. A link of another
 * team's reads as refused rather than as present, which is just as taken.
 */
async function freeToken(): Promise<string> {
  for (let attempt = 0; attempt < 5; attempt++) {
    const token = await newToken()
    try {
      const existing = await getDoc(doc(db, 'shareLinks', token))
      if (!existing.exists()) return token
    } catch {
      // Refused: it exists and belongs to somebody else.
    }
  }
  throw new Error('Could not find an unused link name. Try again.')
}

/**
 * Practical ceiling. Several links exist so different audiences can be revoked
 * independently — a forum post and the family, say — not so a link can be
 * minted per viewer. One link serves any number of people.
 */
export const MAX_LINKS_PER_DASHBOARD = 10

/**
 * How long before an event a link starts being useful, and how long after it
 * stops.
 *
 * Generous on the tail on purpose: people read results for days afterwards, and
 * a link that dies at the chequered flag would be revoked exactly when half its
 * audience got round to opening it. Not indefinite either — a link forwarded to
 * a group chat should not still be a key to the team's timing next season.
 */
const expiresAfterEventMs = 7 * 24 * 60 * 60 * 1000

/**
 * Mint a link to one dashboard, for one event.
 *
 * Scoped to an event rather than to a session or an entry. A session would mean
 * minting a new link every time one ended, which is most of a race weekend; an
 * entry would mean a team running two cars needed two links, or a viewer could
 * only follow one of them. "This weekend, for this team" is the unit people
 * actually share.
 */
export async function mintShareLink(
  dashboardId: string,
  orgId: string,
  uid: string,
  event: { id: string; endsAt: Date | null },
): Promise<string> {
  const token = await freeToken()
  await setDoc(doc(collection(db, 'shareLinks'), token), {
    dashboardId,
    orgId,
    eventId: event.id,
    createdBy: uid,
    createdAt: serverTimestamp(),
    revokedAt: null,
    expiresAt: event.endsAt
      ? Timestamp.fromMillis(event.endsAt.getTime() + expiresAfterEventMs)
      : null,
  })
  return token
}

/**
 * Soft revoke, matching soft delete elsewhere. The Cloud Function tears down
 * the projection as soon as this lands, so access stops immediately while the
 * record of who shared what survives.
 */
export async function revokeShareLink(token: string): Promise<void> {
  await updateDoc(doc(db, 'shareLinks', token), { revokedAt: serverTimestamp() })
}

export function shareUrl(token: string): string {
  return `${window.location.origin}/s/${token}`
}
